import "leaflet/dist/leaflet.css";

import {
  Button,
  Chip,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import {
  Group,
  GroupTitle,
  useDebounce,
  useFieldLabel,
  useTabVisibility,
} from "ra-friendsofbabba";
import { MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm, useFormState } from "react-final-form";

import { OpenStreetMapProvider } from "leaflet-geosearch";
import RoomIcon from "@material-ui/icons/Room";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import { divIcon } from "leaflet";
import { get } from "lodash";
import { makeStyles } from "@material-ui/styles";
import { renderToString } from "react-dom/server";
import { useFieldArray } from "react-final-form-arrays";
import { useSelector } from "react-redux";
import { useTranslate } from "react-admin";

const useStyles = makeStyles((theme) => ({
  container: {
    "& h6": {
      marginTop: theme.spacing(3),
      fontWeight: "bold",
      textTransform: "uppercase",
    },
    "& h6, & .MuiTypography-body2": {
      paddingTop: theme.spacing(1),
      paddingLeft: 16,
      paddingBottom: theme.spacing(1),
    },
    marginBottom: theme.spacing(3),
  },
  searchInput: {
    minWidth: 350,
    marginBottom: theme.spacing(1),
  },
  map: {
    height: 400,
    marginBottom: theme.spacing(1),
    width: "100%",
  },
  bottomBar: {
    textAlign: "right",
    "& .MuiChip-root": {
      marginLeft: theme.spacing(1),
    },
    display: "block",
    clear: "both",
    minHeight: 80,
  },
  removeButton: {
    color: theme.palette.error.main,
  },
  helperText: {
    float: "left",
    width: 450,
    display: "inline-block",
  },
}));
const ClickEvents = ({ onClick }) => {
  useMapEvents({
    click: onClick,
  });
  return null;
};
const PositionMarker = ({ index, position, onClick, onDrag, disabled }) => {
  const markerRef = useRef(null);
  const icon = divIcon({
    className: `transparent`,
    html: renderToString(
      <Fragment>
        <RoomIcon fontSize="large" style={{ fontSize: 80 }} />
        <p className="marker-label">
          <label>#{index + 1}</label>
        </p>
      </Fragment>
    ),
    iconAnchor: [40, 70],
  });

  return position === null ? null : (
    <Marker
      ref={markerRef}
      draggable={!disabled}
      eventHandlers={{
        click() {
          onClick(index);
        },
        dragend() {
          const marker = markerRef.current;
          if (marker !== null) {
            onDrag(index, marker.getLatLng());
          }
        },
      }}
      position={position}
      icon={icon}
    />
  );
};
const PositionInput = ({
  fullWidth,
  defaultCenter = [40.842692, 17.055715],
  defaultZoom = 8,
  disabled,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const formState = useFormState();
  const municipalities = useSelector((state) => {
    const resourceData = state?.admin?.resources?.municipalities?.data;
    const records = Object.keys(resourceData).map((id) => resourceData[id]);
    return records;
  });

  const {
    coords,
    positionQuery,
    positionLat,
    positionLng,
    positionZoom,
    municipalityId,
  } = useMemo(
    () => ({
      positionQuery: get(formState.values, "position_query", ""),
      positionLat:
        get(formState.values, "position_lat", defaultCenter[0]) ||
        defaultCenter[0],
      positionLng:
        get(formState.values, "position_lng", defaultCenter[1]) ||
        defaultCenter[1],
      positionZoom:
        get(formState.values, "position_zoom", defaultZoom) || defaultZoom,
      municipalityId: get(formState.values, "municipality_id"),
      coords: get(formState.values, "coords", []),
    }),
    [formState.values, defaultCenter, defaultZoom]
  );

  const coordsArray = useFieldArray("coords", {
    ...props,
  });

  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(false);
  // const [options, setOptions] = useState([]);
  const [query, setQuery] = useState(positionQuery);
  const [center, setCenter] = useState([positionLat, positionLng]);
  const [zoom, setZoom] = useState(positionZoom);

  const debouncedQuery = useDebounce(query, 500);
  const debouncedCenter = useDebounce(center, 500);
  const debouncedZoom = useDebounce(zoom, 500);

  // useEffect(() => {
  //   async function fetchResults() {
  //     setLoading(true);
  //     const provider = new OpenStreetMapProvider();
  //     const results = await provider.search({ query: debouncedQuery });
  //     setOptions(results.map((r, id) => ({ ...r, id })));
  //     setLoading(false);
  //   }
  //   if (
  //     debouncedQuery &&
  //     debouncedQuery !== null &&
  //     debouncedQuery.length > 0
  //   ) {
  //     fetchResults();
  //   }
  // }, [debouncedQuery]);

  // const handleQueryChange = useCallback(
  //   (_, newQueryText) => setQuery(newQueryText),
  //   []
  // );

  // const handleChange = useCallback(
  //   (_, newValue) => {
  //     if (!newValue || newValue === null) {
  //       return;
  //     }
  //     map.flyTo([newValue.y, newValue.x], 12);
  //     setCenter([newValue.y, newValue.x]);
  //     setZoom(12);
  //   },
  //   [map]
  // );

  useEffect(() => {
    if (loading) {
      return;
    }
    if (query && query !== null && query.length > 0) {
      return;
    }
    const municipality = municipalities.find((m) => m.id === municipalityId);
    if (!municipality) {
      return;
    }

    async function resolvePosition() {
      setLoading(true);
      const provider = new OpenStreetMapProvider();
      const results = await provider.search({ query: municipality.name });
      const firstMatch = results.length > 0 ? results[0] : null;
      if (firstMatch === null) {
        return;
      }
      map.flyTo([firstMatch.y, firstMatch.x], 12);

      setCenter([firstMatch.y, firstMatch.x]);
      setQuery(firstMatch.label);
      setZoom(12);
      setLoading(false);
    }

    resolvePosition();
  }, [municipalityId, query, map, municipalities, loading]);
  useEffect(() => {
    form.batch(() => {
      form.change("position_lat", debouncedCenter[0]);
      form.change("position_lng", debouncedCenter[1]);
      form.change("position_zoom", debouncedZoom);
      form.change("position_query", debouncedQuery);
    });
  }, [debouncedCenter, debouncedZoom, debouncedQuery, form]);

  const handleMove = useCallback(() => {
    if (disabled) {
      return;
    }
    const center = map.getCenter();
    setCenter([center.lat, center.lng]);
    setZoom(map.getZoom());
  }, [map, disabled]);

  useEffect(() => {
    if (!map || disabled) {
      return;
    }
    map.on("move", handleMove);
    return () => {
      map.off("move", handleMove);
    };
  }, [map, disabled, handleMove]);

  const fieldLabel = useFieldLabel({ ...props });
  const translate = useTranslate();
  const visible = useTabVisibility(0);
  useEffect(() => {
    if (map && visible) {
      map.invalidateSize();
    }
  }, [map, visible]);

  const handleClick = useCallback(
    (e) => {
      if (disabled) {
        return;
      }
      coordsArray.fields.push({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
    [coordsArray, disabled]
  );
  const handleMarkerDrag = useCallback(
    (index, latlng) => {
      if (disabled) {
        return;
      }
      coordsArray.fields.update(index, { lat: latlng.lat, lng: latlng.lng });
    },
    [coordsArray, disabled]
  );

  const [marker, setMarker] = useState(null);
  const handleMarkerClick = useCallback(
    (index) => setMarker(disabled ? null : index),
    [disabled]
  );

  const handleMarkerRemove = useCallback(() => {
    if (disabled) {
      return;
    }
    if (marker === null) {
      return;
    }
    coordsArray.fields.remove(marker);
    setMarker(null);
  }, [disabled, coordsArray, marker]);

  return (
    <div>
      <Group wrapper>
        <GroupTitle
          title={fieldLabel("position")}
          subTitle={fieldLabel("position_help")}
        />
      </Group>
      {/* <Autocomplete
        className={classes.searchInput}
        options={options}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(option) => option?.label || query}
        onInputChange={handleQueryChange}
        onChange={handleChange}
        value={query}
        renderInput={(params) => (
          <TextField
            {...params}
            label={fieldLabel("position_query")}
            variant="filled"
          />
        )}
      /> */}
      <MapContainer
        center={center}
        zoom={zoom}
        className={classes.map}
        whenCreated={setMap}
        scrollWheelZoom={false}
      >
        <ClickEvents onClick={handleClick} />
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {coords.map((coord, index) => (
          <PositionMarker
            key={index}
            index={index}
            disabled={disabled}
            position={[coord.lat, coord.lng]}
            onClick={handleMarkerClick}
            onDrag={handleMarkerDrag}
          />
        ))}
      </MapContainer>
      {positionLat && positionLng && (
        <div className={classes.bottomBar}>
          {!disabled && (
            <FormHelperText className={classes.helperText}>
              Clicca sulla mappa per aggiungere un punto.
              <br />
              Clicca su un punto per visualizzare il tasto di rimozione.
              <br />
              Clicca e tieni premuto per trascinare un punto.
            </FormHelperText>
          )}
          {marker !== null && (
            <Button
              size="small"
              color="primary"
              className={classes.removeButton}
              onClick={handleMarkerRemove}
            >
              Rimuovi #{marker + 1}
            </Button>
          )}
          <Chip
            label={translate("resources.projects.fields.position_lat", {
              lat: center[0].toFixed(4),
            })}
          />
          <Chip
            label={translate("resources.projects.fields.position_lng", {
              lng: center[1].toFixed(4),
            })}
          />
          <Chip
            icon={<ZoomInIcon />}
            label={translate("resources.projects.fields.position_zoom", {
              zoom: zoom,
            })}
          />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#ID</TableCell>
                <TableCell>Latitudine</TableCell>
                <TableCell>Longitudine</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coords.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <em>Nessun punto selezionato</em>
                  </TableCell>
                </TableRow>
              )}
              {coords.map((coord, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{coord.lat.toFixed(4)}</TableCell>
                  <TableCell>{coord.lng.toFixed(4)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default PositionInput;
